import { useLanguage } from '@/contexts/LanguageContext';

export default function SustainabilitySolution() {
  const { t } = useLanguage();

  const solutionPoints = [
    {
      icon: 'ri-recycle-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      text: t(
        '降低被焚化與掩埋的廢塑膠量，直接減少廢棄端碳排放。',
        'Reduce the amount of waste plastics incinerated and landfilled, directly decreasing carbon emissions from the waste end.',
        '焼却・埋立される廃プラスチック量を削減し、廃棄側の炭素排出を直接減少させます。'
      ),
    },
    {
      icon: 'ri-leaf-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      text: t(
        '以再生塑膠取代部分原生塑膠，節省原料開採與製造所需能源。',
        'Replace some virgin plastics with recycled plastics, saving energy required for raw material extraction and manufacturing.',
        '再生プラスチックでバージンプラスチックの一部を代替し、原料採掘と製造に必要なエネルギーを節約します。'
      ),
    },
    {
      icon: 'ri-bar-chart-2-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      text: t(
        '導入溯源與數據化管理，讓減碳成果可被量化、被認證、被報告。',
        'Implement traceability and data management, making carbon reduction results quantifiable, certifiable, and reportable.',
        '追跡可能性とデータ管理を導入し、炭素削減成果を定量化・認証・報告可能にします。'
      ),
    },
    {
      icon: 'ri-map-pin-line',
      iconColor: 'text-brand-blue',
      iconBg: 'bg-brand-blue-light',
      text: t(
        '建立在地回收、在地再生、在地應用的 Plastics Reviving 模型。',
        'Establish a Plastics Reviving model of local collection, local recycling, and local application.',
        '地域回収・地域再生・地域応用のPlastics Revivingモデルを構築します。'
      ),
    },
  ];

  return (
    <section className="section-padding bg-white overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="absolute -top-8 -left-8 w-64 h-64 opacity-35 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.18) 0%, transparent 70%)' }} aria-hidden="true" />
            <div className="absolute -bottom-8 -right-8 w-52 h-52 opacity-25 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.15) 0%, transparent 70%)' }} aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ height: '460px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=recycled%20plastic%20circular%20economy%20carbon%20reduction%20pathway%20green%20technology%20sustainable%20manufacturing%20process%20recycling%20loop%20diagram%20visualization%20clean%20industrial%20environment%20green%20and%20blue%20accent%20professional%20photography%20high%20resolution&width=640&height=460&seq=sustain-solution-v1&orientation=portrait"
                alt={t('聚量永續解法 — Plastics Reviving 碳路徑', 'PolyQ Sustainability Solution — Plastics Reviving Carbon Pathway', '聚量サステナビリティソリューション — Plastics Reviving炭素経路')}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white rounded-xl px-5 py-4 border border-neutral-200" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                      <i className="ri-recycle-line text-brand-green text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-neutral-dark text-[1.0625rem]">Plastics Reviving</p>
                      <p className="text-neutral-mid text-[0.9375rem]">
                        {t('在地回收 · 在地再生 · 在地應用', 'Local Collection · Local Recycling · Local Application', '地域回収・地域再生・地域応用')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">
              {t('我們的方法', 'Our Approach', '私たちのアプローチ')}
            </p>
            <h2
              className="font-bold text-neutral-dark mb-5"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
            >
              {t(
                '從「廢棄」端，直接改變碳路徑',
                'Directly Change the Carbon Pathway from the "Waste" End',
                '「廃棄」側から炭素経路を直接変える'
              )}
            </h2>
            <p className="text-neutral-mid mb-8" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
              {t(
                '聚量以 PQernel® 為核心，讓原本被視為問題的廢棄塑膠，轉變為可被再生與再利用的新材料，並結合在地回收與再生體系，打造可被複製的減碳模型。',
                'With PQernel® at its core, PolyQ transforms waste plastics that were once seen as problems into new materials that can be recycled and reused, combined with local collection and recycling systems to create a replicable carbon reduction model.',
                'PQernel®をコアとして、聚量はかつて問題とされていた廃プラスチックを再生・再利用可能な新材料へと転換し、地域の回収・再生システムと組み合わせて複製可能な炭素削減モデルを構築します。'
              )}
            </p>
            <div className="space-y-4">
              {solutionPoints.map((pt, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-[#F5F8FB] border border-neutral-border">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${pt.iconBg} flex-shrink-0`}>
                    <i className={`${pt.icon} ${pt.iconColor} text-xl`}></i>
                  </div>
                  <p className="text-neutral-dark text-[1.0625rem] leading-relaxed pt-1.5">{pt.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
